import { FormsAnswerReview_AnswerFragment, FormsAnswerReview_RevisionFragment } from '../graphql/FormSubmissions.generated';
import { RecursiveFormConfig, RecursiveFormNode } from '../recursiveFormConfig';
import { AnswerReviewEvidence, AnswerReviewFieldViewModel, AnswerReviewValue } from './AnswerReviewWorkbench.types';
import { recursiveReviewField } from './AnswerReviewWorkbench.utils';
import { mapAnswer, mapEvidence } from './formSubmissionAnswers';
import { FormSubmissionAdapterError, getEditor, isChildActive } from './formSubmissionFields';
import { answerTargetKey, emptyAnswerNode, flattenAnswerNodes, readAnswerTree, RecursiveAnswerNode } from './recursiveAnswer';
import { normalizeAnswerTree } from './recursiveAnswerContract';
export function flattenReviewFields(fields: AnswerReviewFieldViewModel[]): AnswerReviewFieldViewModel[] {
  return fields.flatMap(field => [...(field.recursiveNode ? flattenAnswerNodes(field.recursiveNode).map(node => ({
    ...recursiveReviewField(node),
    isActive: field.isActive
  })) : [field]), ...flattenReviewFields(field.children ?? [])]);
}
export function readRecursiveRevision(schema: RecursiveFormNode, revision: FormsAnswerReview_RevisionFragment): RecursiveAnswerNode {
  if (!revision.answerTree) throw new FormSubmissionAdapterError('A recursive revision has no answer tree.');
  const references = new Map<string, AnswerReviewEvidence[]>();
  for (const evidence of revision.evidence ?? []) {
    if (!evidence.target) throw new FormSubmissionAdapterError('A recursive reference has no target.');
    const key = answerTargetKey({
      ...evidence.target,
      arrayItemID: evidence.target.arrayItemID ?? null
    });
    references.set(key, [...(references.get(key) ?? []), ...mapEvidence([evidence], revision.id)]);
  }
  return readAnswerTree(schema, normalizeAnswerTree(revision.answerTree), references);
}
export function recursiveSubmissionSections(config: RecursiveFormConfig, answers: ReadonlyMap<string, FormsAnswerReview_AnswerFragment>, drafts: ReadonlyMap<string, RecursiveAnswerNode>, draftValues: ReadonlyMap<string, AnswerReviewValue> = new Map()) {
  const independentNodes = (node: RecursiveFormNode): RecursiveFormNode[] => [node, ...(node.children ?? []).flatMap(independentNodes)];
  const roots = new Map(config.form.flatMap(section => section.children.flatMap(independentNodes).map(node => [node.id, node] as const)));
  const values = new Map<string, RecursiveAnswerNode>();
  const scalarAnswers = new Map<string, ReturnType<typeof mapAnswer>>();
  for (const root of roots.values()) {
    if (root.fieldType !== 'object' && root.fieldType !== 'array') {
      scalarAnswers.set(root.id, mapAnswer(answers.get(root.id), root, draftValues.get(root.id)));
      continue;
    }
    const revision = answers.get(root.id)?.currentRevision;
    const draft = drafts.get(root.id);
    if (draft) values.set(root.id, draft);else if (revision) values.set(root.id, readRecursiveRevision(root, revision));
  }
  const active = (schema: RecursiveFormNode, visiting = new Set<string>()): boolean => {
    if (!schema.onlyActiveWhen) return true;
    if (visiting.has(schema.id)) throw new FormSubmissionAdapterError('Activation dependency cycle.');
    visiting.add(schema.id);
    const source = roots.get(schema.onlyActiveWhen.fieldId);
    const value = source && scalarAnswers.get(source.id)?.currentRevision;
    // An absent/inactive source is not a present answer with a null value.
    if (!source || !value || !active(source, visiting)) return false;
    if (source.fieldType === 'array' || source.fieldType === 'object') throw new FormSubmissionAdapterError('An activation source must be simple.');
    return isChildActive({
      id: schema.id,
      type: 'field',
      fieldType: 'shortText',
      label: schema.label,
      onlyActiveWhen: schema.onlyActiveWhen
    }, source, value.value);
  };
  const mapField = (schema: RecursiveFormNode): AnswerReviewFieldViewModel => {
    const children = (schema.children ?? []).map(mapField);
    if (schema.fieldType === 'object' || schema.fieldType === 'array') {
      const node = values.get(schema.id) ?? emptyAnswerNode(schema, null);
      return {
        ...recursiveReviewField(node),
        id: schema.id,
        isActive: active(schema),
        recursiveNode: node,
        exportSettings: {
          includeReasoning: schema.export?.includeReasoning ?? false,
          includeQuotes: schema.export?.includeQuotes ?? false,
          includeCitations: schema.export?.includeCitations ?? false
        },
        children
      };
    }
    return {
      id: schema.id,
      label: schema.label,
      isActive: active(schema),
      editor: getEditor(schema),
      // Every scalar independent question is mapped before the view is built.
      ...scalarAnswers.get(schema.id)!,
      exportSettings: {
        includeReasoning: schema.export?.includeReasoning ?? false,
        includeQuotes: schema.export?.includeQuotes ?? false,
        includeCitations: schema.export?.includeCitations ?? false
      },
      userRevisions: [],
      children
    };
  };
  return config.form.map(section => ({
    id: section.id,
    title: section.label,
    fields: section.children.map(mapField)
  }));
}